/**
 * WebGL2 compositor. Renders a `FrameDescription` (see `@ve/core` `resolveFrame`) into a canvas.
 *
 * - Every visual layer is one textured quad; geometry is computed on the CPU (`computeLayerQuad`).
 * - Colours are premultiplied alpha end to end, in linear light: every layer is decoded to linear
 *   (sRGB, or PQ/HLG HDR tone-mapped to SDR) when drawn, the frame is composited in a half-float
 *   framebuffer and an output pass encodes it to sRGB.
 * - Transitions render both sides into pooled framebuffers and blend them with a transition shader.
 * - Nested sequences render recursively into a framebuffer that is then used as a layer texture.
 * - Drawing happens in project pixel coordinates, independent of the drawing-buffer size, so the
 *   preview can render at reduced resolution and the exporter at any output size.
 */
import type { BlendMode, FrameDescription, LayerNode, RenderNode, TransitionNode } from '@ve/core';
import { TextRasterizer } from '../text/text-rasterizer.ts';
import type { TransferFunction } from '../media/color.ts';
import { computeLayerQuad, parseColor, parseLinearColor, type Quad, type Size } from './geometry.ts';
import {
  FULLSCREEN_VS,
  LAYER_FS,
  LAYER_VS,
  OUTPUT_FS,
  TRANSFER_IDS,
  TRANSITION_FS,
  TRANSITION_IDS,
} from './shaders.ts';

/** Clockwise rotation (degrees) that turns the source pixels upright. */
export type TextureRotation = 0 | 90 | 180 | 270;

/** A frame of pixels for a video or image layer, supplied by the preview or export media layer. */
export interface LayerTexture {
  source: TexImageSource;
  width: number;
  height: number;
  /** GPU texture cache slot; defaults to the layer key. */
  cacheKey?: string;
  /** If set and unchanged since the last upload into the slot, the upload is skipped. */
  version?: string | number;
  /** How the pixels are encoded; defaults to `'sdr'` (sRGB). */
  transfer?: TransferFunction;
  /** Rotation still to apply to `source`; `width`/`height` are the upright size. Defaults to 0. */
  rotation?: TextureRotation;
}

export interface TextureProvider {
  /**
   * Returns pixels for a `video`/`image` layer, or `null` if not available yet (the compositor then
   * reuses the last texture uploaded for the layer, if any, to avoid flashing).
   */
  getTexture(layer: LayerNode): LayerTexture | null;
}

export interface LayerGeometry {
  clipId: string;
  /** Corners (TL, TR, BR, BL of the visible region) in project pixel space. */
  corners: Quad;
  sourceWidth: number;
  sourceHeight: number;
}

export interface CompositorOptions {
  /** Needed when the canvas is read back after rendering (export). */
  preserveDrawingBuffer?: boolean;
  /** Generate mipmaps for every upload (better downscaling, slower). */
  highQuality?: boolean;
  onContextRestored?: () => void;
}

interface CachedTexture {
  tex: WebGLTexture;
  width: number;
  height: number;
  version: string | number | undefined;
  transfer: TransferFunction;
  rotation: TextureRotation;
  lastUsed: number;
}

interface Framebuffer {
  fb: WebGLFramebuffer;
  tex: WebGLTexture;
  width: number;
  height: number;
}

interface Programs {
  layer: WebGLProgram;
  transition: WebGLProgram;
  output: WebGLProgram;
  layerVao: WebGLVertexArrayObject;
  layerBuf: WebGLBuffer;
  fullVao: WebGLVertexArrayObject;
  u: {
    resolution: WebGLUniformLocation | null;
    tex: WebGLUniformLocation | null;
    opacity: WebGLUniformLocation | null;
    flipY: WebGLUniformLocation | null;
    solid: WebGLUniformLocation | null;
    color: WebGLUniformLocation | null;
    transfer: WebGLUniformLocation | null;
    srgbBlending: WebGLUniformLocation | null;
    scene: WebGLUniformLocation | null;
    outputSrgbBlending: WebGLUniformLocation | null;
    from: WebGLUniformLocation | null;
    to: WebGLUniformLocation | null;
    progress: WebGLUniformLocation | null;
    type: WebGLUniformLocation | null;
    aspect: WebGLUniformLocation | null;
  };
}

/** Textures unused for this many renders are freed. */
const TEXTURE_TTL_RENDERS = 90;

export class Compositor {
  private gl: WebGL2RenderingContext;
  private programs!: Programs;
  private textures = new Map<string, CachedTexture>();
  private fboFree: Framebuffer[] = [];
  private fboAll: Framebuffer[] = [];
  private renderCount = 0;
  private lost = false;
  /** Whether half-float framebuffers are renderable (EXT_color_buffer_float); else 8-bit. */
  private floatTargets = false;
  private projectSize: Size = { width: 1, height: 1 };
  /** Blending in gamma-encoded sRGB rather than linear light (see `FrameDescription.blending`). */
  private srgbBlending = false;
  private currentTarget: Framebuffer | null = null;
  private geometry = new Map<string, LayerGeometry>();
  private readonly text = new TextRasterizer();
  private readonly vertexData = new Float32Array(16);

  constructor(
    private readonly canvas: HTMLCanvasElement | OffscreenCanvas,
    private readonly options: CompositorOptions = {},
  ) {
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: true,
      preserveDrawingBuffer: options.preserveDrawingBuffer ?? false,
      powerPreference: 'high-performance',
    }) as WebGL2RenderingContext | null;
    if (!gl) throw new Error('WebGL2 is not supported by this browser');
    this.gl = gl;
    this.init();
    const target = canvas as EventTarget;
    target.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.lost = true;
    });
    target.addEventListener('webglcontextrestored', () => {
      this.textures.clear();
      this.fboAll = [];
      this.fboFree = [];
      this.init();
      this.lost = false;
      this.options.onContextRestored?.();
    });
  }

  /** Geometry of the top-level layers drawn in the last frame, by clip id. */
  getLayerGeometry(clipId: string): LayerGeometry | null {
    return this.geometry.get(clipId) ?? null;
  }

  get isContextLost(): boolean {
    return this.lost || this.gl.isContextLost();
  }

  render(desc: FrameDescription, provider: TextureProvider): void {
    if (this.isContextLost) return;
    const gl = this.gl;
    this.renderCount++;
    this.geometry.clear();
    this.projectSize = { width: desc.width, height: desc.height };
    this.srgbBlending = desc.blending === 'srgb';
    const scene = this.acquireFbo();
    this.bindTarget(scene);
    const [r, g, b] = this.workingColor(desc.background);
    gl.clearColor(r, g, b, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.enable(gl.BLEND);
    this.drawNodes(desc.layers, provider, 0);
    this.drawOutput(scene);
    this.releaseFbo(scene);
    this.evictTextures();
  }

  dispose(): void {
    const gl = this.gl;
    for (const t of this.textures.values()) gl.deleteTexture(t.tex);
    for (const f of this.fboAll) {
      gl.deleteFramebuffer(f.fb);
      gl.deleteTexture(f.tex);
    }
    this.textures.clear();
    this.fboAll = [];
    this.fboFree = [];
    this.text.clear();
    // A DOM canvas may be reused by a new compositor (e.g. React StrictMode remounts), and
    // getContext() would hand it the same — then lost — context. Only release contexts we own.
    if (typeof OffscreenCanvas !== 'undefined' && this.canvas instanceof OffscreenCanvas) {
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    }
  }

  // -------------------------------------------------------------------------------------------

  private init(): void {
    const gl = this.gl;
    const layer = this.link(LAYER_VS, LAYER_FS);
    const transition = this.link(FULLSCREEN_VS, TRANSITION_FS);
    const output = this.link(FULLSCREEN_VS, OUTPUT_FS);
    this.floatTargets = !!gl.getExtension('EXT_color_buffer_float');

    const layerVao = gl.createVertexArray()!;
    const layerBuf = gl.createBuffer()!;
    gl.bindVertexArray(layerVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, layerBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.vertexData.byteLength, gl.DYNAMIC_DRAW);
    const aPos = gl.getAttribLocation(layer, 'aPos');
    const aUv = gl.getAttribLocation(layer, 'aUv');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(aUv);
    gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 16, 8);

    const fullVao = gl.createVertexArray()!;
    const fullBuf = gl.createBuffer()!;
    gl.bindVertexArray(fullVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, fullBuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, 1, 1, -1, 1]), gl.STATIC_DRAW);
    const aFull = gl.getAttribLocation(transition, 'aPos');
    gl.enableVertexAttribArray(aFull);
    gl.vertexAttribPointer(aFull, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);

    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);

    this.programs = {
      layer,
      transition,
      output,
      layerVao,
      layerBuf,
      fullVao,
      u: {
        resolution: gl.getUniformLocation(layer, 'uResolution'),
        tex: gl.getUniformLocation(layer, 'uTex'),
        opacity: gl.getUniformLocation(layer, 'uOpacity'),
        flipY: gl.getUniformLocation(layer, 'uFlipY'),
        solid: gl.getUniformLocation(layer, 'uSolid'),
        color: gl.getUniformLocation(layer, 'uColor'),
        transfer: gl.getUniformLocation(layer, 'uTransfer'),
        srgbBlending: gl.getUniformLocation(layer, 'uSrgbBlending'),
        scene: gl.getUniformLocation(output, 'uScene'),
        outputSrgbBlending: gl.getUniformLocation(output, 'uSrgbBlending'),
        from: gl.getUniformLocation(transition, 'uFrom'),
        to: gl.getUniformLocation(transition, 'uTo'),
        progress: gl.getUniformLocation(transition, 'uProgress'),
        type: gl.getUniformLocation(transition, 'uType'),
        aspect: gl.getUniformLocation(transition, 'uAspect'),
      },
    };
  }

  private link(vsSrc: string, fsSrc: string): WebGLProgram {
    const gl = this.gl;
    const compile = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        throw new Error(`Shader compile error: ${gl.getShaderInfoLog(s) ?? 'unknown'}`);
      }
      return s;
    };
    const p = gl.createProgram()!;
    gl.attachShader(p, compile(gl.VERTEX_SHADER, vsSrc));
    gl.attachShader(p, compile(gl.FRAGMENT_SHADER, fsSrc));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      throw new Error(`Program link error: ${gl.getProgramInfoLog(p) ?? 'unknown'}`);
    }
    return p;
  }

  private bufferSize(): Size {
    return { width: this.gl.drawingBufferWidth, height: this.gl.drawingBufferHeight };
  }

  private bindTarget(target: Framebuffer | null): void {
    const gl = this.gl;
    this.currentTarget = target;
    gl.bindFramebuffer(gl.FRAMEBUFFER, target ? target.fb : null);
    const size = target ?? this.bufferSize();
    gl.viewport(0, 0, size.width, size.height);
  }

  private acquireFbo(): Framebuffer {
    const gl = this.gl;
    const { width, height } = this.bufferSize();
    const idx = this.fboFree.findIndex((f) => f.width === width && f.height === height);
    if (idx !== -1) return this.fboFree.splice(idx, 1)[0]!;
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    if (this.floatTargets) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, width, height, 0, gl.RGBA, gl.HALF_FLOAT, null);
    } else {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    }
    this.setTexParams(false);
    const fb = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.currentTarget ? this.currentTarget.fb : null);
    const fbo = { fb, tex, width, height };
    this.fboAll.push(fbo);
    return fbo;
  }

  private releaseFbo(fbo: Framebuffer): void {
    this.fboFree.push(fbo);
  }

  /** Renders nodes into a fresh transparent framebuffer and returns it (caller releases). */
  private renderToFbo(
    nodes: Array<RenderNode | null>,
    provider: TextureProvider,
    depth: number,
  ): Framebuffer {
    const gl = this.gl;
    const previous = this.currentTarget;
    const fbo = this.acquireFbo();
    this.bindTarget(fbo);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    this.drawNodes(
      nodes.filter((n): n is RenderNode => n !== null),
      provider,
      depth,
    );
    this.bindTarget(previous);
    return fbo;
  }

  private drawNodes(nodes: RenderNode[], provider: TextureProvider, depth: number): void {
    for (const node of nodes) {
      if (node.kind === 'layer') this.drawLayer(node, provider, depth);
      else this.drawTransition(node, provider, depth);
    }
  }

  private drawTransition(node: TransitionNode, provider: TextureProvider, depth: number): void {
    const gl = this.gl;
    const a = this.renderToFbo([node.from], provider, depth);
    const b = this.renderToFbo([node.to], provider, depth);
    const { transition, fullVao, u } = this.programs;
    gl.useProgram(transition);
    gl.bindVertexArray(fullVao);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, a.tex);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, b.tex);
    gl.uniform1i(u.from, 0);
    gl.uniform1i(u.to, 1);
    gl.uniform1f(u.progress, node.progress);
    gl.uniform1i(u.type, TRANSITION_IDS[node.type]);
    gl.uniform1f(u.aspect, this.projectSize.width / this.projectSize.height);
    this.setBlend('normal');
    gl.drawArrays(gl.TRIANGLE_FAN, 0, 4);
    gl.activeTexture(gl.TEXTURE0);
    this.releaseFbo(a);
    this.releaseFbo(b);
  }

  /** A colour in the working space. */
  private workingColor(hex: string): [number, number, number, number] {
    return this.srgbBlending ? parseColor(hex) : parseLinearColor(hex);
  }

  /** Encodes the scene into the drawing buffer. */
  private drawOutput(scene: Framebuffer): void {
    const gl = this.gl;
    const { output, fullVao, u } = this.programs;
    this.bindTarget(null);
    gl.disable(gl.BLEND);
    gl.useProgram(output);
    gl.bindVertexArray(fullVao);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, scene.tex);
    gl.uniform1i(u.scene, 0);
    gl.uniform1i(u.outputSrgbBlending, this.srgbBlending ? 1 : 0);
    gl.drawArrays(gl.TRIANGLE_FAN, 0, 4);
  }

  private drawLayer(layer: LayerNode, provider: TextureProvider, depth: number): void {
    if (layer.opacity <= 0) return;
    const gl = this.gl;
    const canvas = this.projectSize;
    let tex: WebGLTexture | null = null;
    let size: Size;
    let flipY = false;
    let transfer: number = TRANSFER_IDS.srgb;
    let rotation: TextureRotation = 0;
    let solid: [number, number, number, number] | null = null;
    let nested: Framebuffer | null = null;

    switch (layer.source.type) {
      case 'solid':
        solid = this.workingColor(layer.source.color);
        size = canvas;
        break;
      case 'sequence':
        nested = this.renderToFbo(layer.source.layers, provider, depth + 1);
        tex = nested.tex;
        size = canvas;
        flipY = true;
        transfer = TRANSFER_IDS.working;
        break;
      case 'text': {
        const r = this.text.rasterize(layer.source.text, layer.source.style);
        const cached = this.upload(`text:${layer.key}`, r, r.canvas, true);
        tex = cached.tex;
        size = r;
        break;
      }
      case 'video':
      case 'image': {
        const t = provider.getTexture(layer);
        const key = t?.cacheKey ?? layer.key;
        const cached = t
          ? this.upload(key, t, t.source, layer.source.type === 'image')
          : this.touch(layer.key);
        if (!cached) return;
        tex = cached.tex;
        size = cached;
        transfer = TRANSFER_IDS[cached.transfer === 'sdr' ? 'srgb' : cached.transfer];
        rotation = cached.rotation;
        break;
      }
    }

    const quad = computeLayerQuad(layer.transform, layer.fit, size, canvas);
    if (depth === 0) {
      this.geometry.set(layer.clipId, {
        clipId: layer.clipId,
        corners: quad.corners,
        sourceWidth: size.width,
        sourceHeight: size.height,
      });
    }

    const { layer: program, layerVao, layerBuf, u } = this.programs;
    gl.useProgram(program);
    gl.bindVertexArray(layerVao);
    const v = this.vertexData;
    const [tl, tr, br, bl] = quad.corners;
    const { u0, v0, u1, v1 } = quad.uv;
    const [uvTl, uvTr, uvBr, uvBl] = [
      rotateUv(u0, v0, rotation),
      rotateUv(u1, v0, rotation),
      rotateUv(u1, v1, rotation),
      rotateUv(u0, v1, rotation),
    ];
    v.set([tl.x, tl.y, ...uvTl, tr.x, tr.y, ...uvTr, br.x, br.y, ...uvBr, bl.x, bl.y, ...uvBl]);
    gl.bindBuffer(gl.ARRAY_BUFFER, layerBuf);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, v);
    gl.uniform2f(u.resolution, canvas.width, canvas.height);
    gl.uniform1f(u.opacity, layer.opacity);
    gl.uniform1i(u.flipY, flipY ? 1 : 0);
    gl.uniform1i(u.solid, solid ? 1 : 0);
    gl.uniform1i(u.transfer, transfer);
    gl.uniform1i(u.srgbBlending, this.srgbBlending ? 1 : 0);
    if (solid) gl.uniform4f(u.color, solid[0], solid[1], solid[2], solid[3]);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.uniform1i(u.tex, 0);
    this.setBlend(layer.blendMode);
    gl.drawArrays(gl.TRIANGLE_FAN, 0, 4);
    if (nested) this.releaseFbo(nested);
  }

  private setBlend(mode: BlendMode): void {
    const gl = this.gl;
    switch (mode) {
      case 'normal':
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        break;
      case 'add':
        gl.blendFunc(gl.ONE, gl.ONE);
        break;
      case 'screen':
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_COLOR);
        break;
      case 'multiply':
        gl.blendFuncSeparate(gl.DST_COLOR, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        break;
    }
  }

  private setTexParams(mipmap: boolean): void {
    const gl = this.gl;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, mipmap ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  private touch(key: string): CachedTexture | null {
    const cached = this.textures.get(key);
    if (cached) cached.lastUsed = this.renderCount;
    return cached ?? null;
  }

  private upload(
    key: string,
    t: Pick<LayerTexture, 'width' | 'height' | 'version' | 'transfer' | 'rotation'>,
    source: TexImageSource,
    isStatic: boolean,
  ): CachedTexture {
    const gl = this.gl;
    const { width, height, version } = t;
    const transfer = t.transfer ?? 'sdr';
    const rotation = t.rotation ?? 0;
    let cached = this.textures.get(key);
    if (cached && version !== undefined && cached.version === version) {
      cached.lastUsed = this.renderCount;
      return cached;
    }
    const hdr = transfer !== 'sdr';
    // Half-float mipmaps need renderable float textures.
    const mipmap = (isStatic || !!this.options.highQuality) && (!hdr || this.floatTargets);
    if (!cached) {
      cached = {
        tex: gl.createTexture()!,
        width,
        height,
        version,
        transfer,
        rotation,
        lastUsed: this.renderCount,
      };
      this.textures.set(key, cached);
    }
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, cached.tex);
    // HDR frames arrive as the raw 10-bit signal; 8 bits would band after decoding to linear.
    if (hdr) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT, source);
    else gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    this.setTexParams(mipmap);
    if (mipmap) gl.generateMipmap(gl.TEXTURE_2D);
    cached.width = width;
    cached.height = height;
    cached.version = version;
    cached.transfer = transfer;
    cached.rotation = rotation;
    cached.lastUsed = this.renderCount;
    return cached;
  }

  private evictTextures(): void {
    for (const [key, t] of this.textures) {
      if (this.renderCount - t.lastUsed > TEXTURE_TTL_RENDERS) {
        this.gl.deleteTexture(t.tex);
        this.textures.delete(key);
      }
    }
    // Free framebuffers whose size no longer matches (resolution changed).
    const { width, height } = this.bufferSize();
    this.fboFree = this.fboFree.filter((f) => {
      if (f.width === width && f.height === height) return true;
      this.gl.deleteFramebuffer(f.fb);
      this.gl.deleteTexture(f.tex);
      this.fboAll.splice(this.fboAll.indexOf(f), 1);
      return false;
    });
  }
}

/** Maps upright texture coordinates to those of a source that still needs `rotation` (clockwise). */
function rotateUv(u: number, v: number, rotation: TextureRotation): [number, number] {
  switch (rotation) {
    case 0:
      return [u, v];
    case 90:
      return [v, 1 - u];
    case 180:
      return [1 - u, 1 - v];
    case 270:
      return [1 - v, u];
  }
}
